const formatNumber = (num) => String(num).padStart(2, '0');
const generateId = (prefix = 'dropdown') => `${prefix}-${Math.random().toString(36).substring(2, 6)}`;

const createDropdown = ({ idPrefix, label, options, optionPrefix }) => {
  const dropdownId = generateId(idPrefix);

  const optionsHTML = options.map(({ value, isActive }) => {
    const display = formatNumber(value);
    const activeClass = isActive ? ' is-active' : '';
    return `
      <li id="${optionPrefix}-${value}" class="aena-dropdown__option aena-dropdown__option--large${activeClass}" 
          role="option" aria-selected="${isActive}" data-value="${value}">
        ${display}
      </li>`;
  }).join('');

  return `
    <div class="aena-dropdown aena-dropdown--${idPrefix}" id="${dropdownId}">
      <button 
        aria-controls="aena-dropdown-panel-${dropdownId}"
        aria-expanded="false"
        aria-haspopup="listbox"
        class="aena-dropdown__trigger aena-input aena-input--large"
        id="aena-dropdown-trigger-${dropdownId}"
        role="combobox"
        tabindex="0"
        type="button"
        disabled
      >
        <span class="aena-dropdown__label">${formatNumber(label)}</span>
        <svg class="aena-sprite aena-dropdown__toggle" aria-hidden="true" focusable="false">
          <use href="/o/aena-theme-spritemap/spritemap.svg#digital-flecha-abajo-1"></use>
        </svg>
      </button>

      <ul 
        aria-labelledby="aena-dropdown-trigger-${dropdownId}"
        class="aena-dropdown__panel"
        id="aena-dropdown-panel-${dropdownId}"
        role="listbox"
        tabindex="-1"
      >
        ${optionsHTML}
      </ul>
    </div>
  `;
};

const createHourDropdown = (minHour, maxHour) => {
  const hours = Array.from({ length: maxHour - minHour + 1 }, (_, i) => ({
    value: minHour + i,
    isActive: i === 0
  }));

  const html = createDropdown({
    idPrefix: 'hour',
    label: minHour,
    options: hours,
    optionPrefix: 'hour'
  });

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html.trim();
  return wrapper.firstElementChild;
};

const createMinuteDropdown = (interval) => {
  const minutes = Array.from({ length: 60 / interval }, (_, i) => ({
    value: i * interval,
    isActive: i === 0
  }));

  const html = createDropdown({
    idPrefix: 'minute',
    label: 0,
    options: minutes,
    optionPrefix: 'minute'
  });

  const wrapper = document.createElement("div");
  wrapper.innerHTML = html.trim();
  return wrapper.firstElementChild;
};

const formatTimeUnit = (value) => value !== undefined ? value.toString().padStart(2, "0") : "";

const markActiveOption = (dropdown, value) => {
  dropdown.querySelectorAll(".aena-dropdown__option").forEach(opt => {
    opt.classList.remove("is-active");
    if (opt.getAttribute("data-value") === String(value)) {
      opt.classList.add("is-active");
      const label = dropdown.querySelector(".aena-dropdown__label");
      if (label) label.textContent = formatTimeUnit(value);
    }
  });
};

const updateDateTimeDisplay = (instance, dateDisplay) => {
  const selectedDate = instance.selectedDates[0];
  if (!selectedDate || !dateDisplay) return;

  const formattedDate = instance.formatDate(selectedDate, "d-m-Y");
  const hour = instance.config.selectedHour ?? instance.config.currentHour ?? "";
  const minute = instance.config.selectedMinute ?? instance.config.currentMinute ?? "";
  dateDisplay.textContent = `${formattedDate} a las ${formatTimeUnit(hour)}:${formatTimeUnit(minute)}`;
};

export const initFlatpickr = (selector, setDateState, minuteInterval, minHour, maxHour, locale) => {
  flatpickr(selector, {
    dateFormat: "d-m-Y H:i",
    allowInput: true,
    closeOnSelect: false,
    disableMobile: true,
    locale: locale,
    // minDate: "today",
    // maxDate: new Date(new Date().getFullYear() + 1, 11, 31),
    // minuteIncrement: 5,

    onOpen: function (selectedDates, dateStr, instance) {
      const label = instance.calendarContainer.querySelector(".paragraph-semibold-small");
      //console.info("Fulldate: " + JSON.stringify(instance.config));
      instance.config.selectedHour = null;
      instance.config.selectedMinute = null;
      instance.config.hourSelected = false;
      instance.config.minuteSelected = false;

      label.textContent = instance.config.fullDate || "Selecciona una fecha";

      const hour = instance.config.currentHour;
      const minute = instance.config.currentMinute;

      const hourDropdown = instance.calendarContainer.querySelector(".aena-dropdown--hour");
      const minuteDropdown = instance.calendarContainer.querySelector(".aena-dropdown--minute");

      if (hourDropdown && hour != null) markActiveOption(hourDropdown, hour);
      if (minuteDropdown && minute != null) markActiveOption(minuteDropdown, minute);
    },

    onChange: function (selectedDates, dateStr, instance) {
      instance.input.value = instance.config.fullDate || "";

      const timingInputs = instance.calendarContainer.querySelectorAll(".flatpickr-custom-timepicker .aena-input");
      timingInputs.forEach(input => input.removeAttribute("disabled"));

      const dateDisplay = instance.calendarContainer.querySelector(".paragraph-semibold-small");
      if (dateDisplay && selectedDates.length > 0) {
        updateDateTimeDisplay(instance, dateDisplay);
      }
    },

    onReady: function (selectedDates, dateStr, instance) {
      const container = instance.calendarContainer;
      container.querySelectorAll(".flatpickr-custom-timepicker, .flatpickr-custom-footer").forEach(el => el.remove());

      const timePicker = document.createElement("div");
      timePicker.className = "flatpickr-custom-timepicker";
      timePicker.innerHTML = `
        <svg class="aena-sprite" aria-hidden="true" focusable="false">
          <use href="/o/aena-theme-spritemap/spritemap.svg#digital-horarios"></use>
        </svg>
        ${createHourDropdown(minHour, maxHour).outerHTML}
        :
        ${createMinuteDropdown(minuteInterval).outerHTML}
      `;

      const footer = document.createElement("div");
      footer.className = "flatpickr-custom-footer";
      footer.innerHTML = `
        <div class="flatpickr-custom-footer__details">
          <p class="paragraph-semibold-xsmall">Fecha y hora elegidas</p>
          <p class="paragraph-semibold-small">Selecciona una fecha</p>
        </div>
        <button disabled type="button" class="aena-button aena-button--primary aena-button--small">Aceptar</button>
      `;

      container.appendChild(timePicker);
      container.appendChild(footer);

      const acceptButton = container.querySelector(".aena-button");
      const dateDisplay = container.querySelector(".paragraph-semibold-small");

      acceptButton.addEventListener("click", function () {
        const selectedDate = instance.selectedDates[0];
        if (!selectedDate) return;

        const hour = instance.config.selectedHour ?? instance.config.currentHour;
        const minute = instance.config.selectedMinute ?? instance.config.currentMinute;

        if (hour != null && minute != null) {
          selectedDate.setHours(parseInt(hour));
          selectedDate.setMinutes(parseInt(minute));

          const fullDate = instance.formatDate(selectedDate, "d-m-Y H:i");
          setDateState(fullDate);
          instance.setDate(fullDate);
          instance.config.currentHour = hour;
          instance.config.currentMinute = minute;
          instance.config.currentDate = selectedDate;
          instance.config.fullDate = fullDate;
          instance.close();
        }
      });

      container.addEventListener("click", (e) => {
        const option = e.target.closest(".aena-dropdown__option");
        const dropdown = option?.closest(".aena-dropdown");
        if (!option || !dropdown) return;

        const isHour = dropdown.id.startsWith("hour");
        const isMinute = dropdown.id.startsWith("minute");

        if (isHour) {
          instance.config.hourSelected = true;
          instance.config.selectedHour = option.getAttribute("data-value");
        }

        if (isMinute) {
          instance.config.minuteSelected = true;
          instance.config.selectedMinute = option.getAttribute("data-value");
        }

        markActiveOption(dropdown, option.dataset.value);
        updateDateTimeDisplay(instance, dateDisplay);

        if (instance.config.hourSelected && instance.config.minuteSelected) {
          acceptButton.removeAttribute("disabled");
        }
      });
    }
  });
};
